/**
 * Role Management Component
 *
 * Comprehensive role management with permissions
 * Features:
 * - Create/Edit/Delete roles
 * - Set a role's access to every module (View / Create / Edit / Delete) in the same dialog
 * - View users assigned to each role
 * - Role hierarchies
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Divider,
  Checkbox,
  Badge,
  Descriptions,
  Typography,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SafetyOutlined,
  UserOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import { toast } from "react-toastify";
import { get_roles, add_role, delete_role, get_api_function, update_access_list_admin } from '@/utils/api';
import RoleAccessMatrix from '@/components/RoleAccessMatrix';
import { viewOnly } from '@/utils/accessMatrix';
import { accessControlUtils } from '@/components/AccessControlUtils';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const RoleManagement = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  // The role's access to every module, as the Access Control page used to show it
  const [accessRows, setAccessRows] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  const access = accessControlUtils("User Management");
  const canEditAccess = !!accessControlUtils("Access Control")?.update_access;

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      await get_roles((res) => {
        if (res.data && res.data.data) {
          setRoles(res.data.data);
        }
      });
    } catch (error) {
      toast.error("Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  // A role's access rows: its own, or for a new role every module with View only (what a new role has always had).
  const loadAccess = (roleId) => {
    setAccessLoading(true);
    setAccessRows([]);
    const url = roleId ? `/bp_get_access?role_id=${roleId}` : "/bp_get_access?modules_only=1";
    get_api_function(url, (res) => {
      const rows = res?.data?.status === "success" ? Object.values(res.data.data[0]).find(Array.isArray) || [] : [];
      setAccessRows(roleId ? rows : viewOnly(rows));
      setAccessLoading(false);
    });
  };

  const handleAdd = () => {
    setEditingRole(null);
    form.resetFields();
    loadAccess(null);
    setModalVisible(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    form.setFieldsValue({
      role_name: role.role,
      description: role.description,
      is_active: role.is_active !== false,
    });
    loadAccess(role.role_id);
    setModalVisible(true);
  };

  const handleDelete = async (roleId) => {
    try {
      await delete_role({ id: roleId }, (res) => {
        if (res.status === "success") {
          toast.success("Role deleted successfully");
          loadRoles();
        } else {
          toast.error(res.message || "Failed to delete role");
        }
      });
    } catch (error) {
      toast.error("Failed to delete role");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const roleData = {
        role_name: values.role_name,
        description: values.description,
        is_active: values.is_active !== false,
        ...(editingRole && { id: editingRole.role_id }),
      };

      await add_role(roleData, (res) => {
        if (res.status === "success") {
          const roleId = editingRole ? editingRole.role_id : res.data?.role_id;
          const finish = (message) => {
            toast.success(message);
            setModalVisible(false);
            form.resetFields();
            loadRoles();
          };
          const done = editingRole ? "Role updated successfully" : "Role created successfully";
          // Save the access the dialog shows, as the Access Control page did.
          if (canEditAccess && roleId && accessRows.length) {
            update_access_list_admin(accessRows.map((row) => ({ ...row, role_id: roleId })), (accessRes) => {
              if (accessRes?.status === "success") finish(done);
              else toast.warning(`${done}, but its access was not saved: ${accessRes?.message || "please try again"}`);
            });
          } else {
            finish(done);
          }
        } else {
          toast.error(res.message || "Failed to save role");
        }
      });
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const filteredRoles = roles.filter(role =>
    role.role?.toLowerCase().includes(searchText.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: "Role Name",
      dataIndex: "role",
      key: "role",
      width: "20%",
      render: (text, record) => (
        <Space>
          <SafetyOutlined style={{ color: "#1890ff" }} />
          <strong>{text}</strong>
          {!record.is_active && <Tag color="red">Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (text) => text || <span style={{ color: "#999" }}>No description</span>,
    },
    {
      title: "Access",
      key: "access",
      width: "25%",
      render: (_, record) =>
        record.modules_count > 0 ? (
          <Tag color="blue" style={{ fontSize: 11 }}>{record.modules_count} modules</Tag>
        ) : (
          <span style={{ color: "#999" }}>No access</span>
        ),
    },
    {
      title: "Users",
      key: "users",
      width: "10%",
      align: "center",
      render: (_, record) => (
        <Tooltip title="Number of users with this role">
          <Badge count={record.user_count || 0} showZero>
            <UserOutlined style={{ fontSize: 18, color: "#1890ff" }} />
          </Badge>
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "15%",
      align: "center",
      render: (_, record) => (
        <Space>
          {access?.update_access && (
            <Tooltip title="Edit Role">
              <Button
                type="link"
                icon={<EditOutlined style={{ fontSize: '20px' }} />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          {access?.delete_access && (
            <Popconfirm
              title="Delete this role?"
              description="Users with this role will lose their permissions."
              onConfirm={() => handleDelete(record.role_id)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{
                className: "bg-red-600 text-white hover:bg-red-700",
              }}
            >
              <Tooltip title="Delete Role">
                <Button type="link" danger icon={<DeleteOutlined style={{ fontSize: '20px' }} />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      className="shadow-sm"
      bordered={false}
      style={{ backgroundColor: cardBgColor }}
      title={
        <div className="flex items-center gap-2">
          <Icons.Shield className="w-7 h-7 text-blue-600" />
          <Title
            level={4}
            style={{
              color: textColor,
              margin: 0,
              padding: 0,
            }}
          >
            {t('Role Management')}
          </Title>
        </div>
      }
      extra={
        <Space>
          <Input
            placeholder="Search roles..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
          {access?.create_access && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
                borderColor: isDark ? '#0F172A' : defaultColors.primary,
              }}
            >
              Add Role
            </Button>
          )}
        </Space>
      }
    >
      <Table
        dataSource={filteredRoles}
        columns={columns}
        rowKey="role_id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} roles`,
        }}
        size="middle"
      />

      <Modal
        title={
          <Space>
            <SafetyOutlined />
            {editingRole ? "Edit Role" : "Create New Role"}
          </Space>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={handleSubmit}
        width={760}
        okText={editingRole ? "Update" : "Create"}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ is_active: true }}
        >
          <Form.Item
            name="role_name"
            label="Role Name"
            rules={[
              { required: true, message: "Please enter role name" },
              { min: 2, message: "Role name must be at least 2 characters" },
            ]}
          >
            <Input placeholder="e.g., Production Manager, QC Analyst" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={3}
              placeholder="Describe the responsibilities and purpose of this role..."
            />
          </Form.Item>

          <Divider>Permissions</Divider>

          <div style={{ fontWeight: 500, marginBottom: 8 }}>
            Select Permissions
            {!canEditAccess && (
              <span style={{ fontWeight: 400, color: "#999", marginLeft: 8 }}>
                (you can view a role's access but not change it)
              </span>
            )}
          </div>
          <RoleAccessMatrix
            rows={accessRows}
            onChange={setAccessRows}
            disabled={!canEditAccess}
            loading={accessLoading}
          />

          <Form.Item
            name="is_active"
            valuePropName="checked"
          >
            <Checkbox>Active</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default RoleManagement;
